// Daily-reload scheduling (docs/04 self-healing: "memory creep on weak WebView").
//
// Once a day at a quiet hour the whole page reloads to shed leaked memory and to
// apply a pending Service Worker update. The exact minute is offset by a
// DEVICE-STABLE pseudo-random amount so a whole fleet never reloads at the same
// instant (which would look like an outage). The offset is derived from the
// device token hash — same device → same offset across boots, different devices
// → spread across the window.

/** Quiet hour, local time, around which reloads are scheduled. */
export const RELOAD_HOUR_LOCAL = 4;
/** Reload happens at RELOAD_HOUR_LOCAL:00 ± up to this many ms (default ±30 min). */
export const RELOAD_JITTER_MS = 30 * 60 * 1000;

/**
 * FNV-1a 32-bit hash — tiny, dependency-free, good enough for spreading a fleet.
 * Not cryptographic; only used to turn a token into a stable number.
 */
export function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    // h *= 16777619, kept in 32-bit via Math.imul.
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Device-stable offset in [-jitter, +jitter] derived from the token.
 * A missing/empty token yields 0 (reload exactly on the hour) — acceptable
 * because an unpaired device isn't in RUNNING and won't schedule reloads.
 */
export function dailyReloadOffsetMs(token: string, jitterMs: number = RELOAD_JITTER_MS): number {
  if (!token) return 0;
  const h = hash32(token);
  // Map [0, 2^32) → [-jitter, +jitter].
  const unit = h / 0xffffffff; // [0, 1]
  return Math.round((unit * 2 - 1) * jitterMs);
}

/**
 * Delay in ms from `now` until the next scheduled reload instant
 * (RELOAD_HOUR_LOCAL:00 local + offset). Always strictly positive: if today's
 * instant has already passed, returns the delay to tomorrow's.
 */
export function msUntilNextReload(
  token: string,
  now: number = Date.now(),
  hourLocal: number = RELOAD_HOUR_LOCAL,
  jitterMs: number = RELOAD_JITTER_MS,
): number {
  const offset = dailyReloadOffsetMs(token, jitterMs);
  const d = new Date(now);
  const target = new Date(now);
  target.setHours(hourLocal, 0, 0, 0);
  let t = target.getTime() + offset;
  if (t <= now) {
    // Move to tomorrow.
    target.setDate(d.getDate() + 1);
    t = target.getTime() + offset;
  }
  return t - now;
}
